import useEventListener from "@/hooks/useEventListener";
import { useApp, useAppStore } from "@/store/app";
import { useReplicacheSongs } from "@/store/replicache";
import type { SongId } from "@/utils/types";
import { useParams } from "next/navigation";
import { useCallback, useEffect, useRef, useState } from "react";
import { MdPlayCircleOutline, MdReplay, MdStop } from "react-icons/md";
import shallow from "zustand/shallow";

const lyricLineHeight = 60;
const highlightLines = 6; // how many lines are in the `playFocusHighlight`
const outerLines = 0;
const scrimHeight = lyricLineHeight * (highlightLines + outerLines);

const delayKickoffSec = 3;
const delayKickoffMs = delayKickoffSec * 1000;
const delayScrollMs = 20;

const LOG_TAG = "SongViewAutoPlay";
const LOG_VERBOSE = true;

export default function AutoPlay({
  durationSeconds,
  scrollElement,
}: {
  durationSeconds: number;
  scrollElement: HTMLElement;
}) {
  const { setIsAutoPlaying: appSetIsAutoPlaying } = useAppStore(
    ({ setIsAutoPlaying }) => ({
      setIsAutoPlaying,
    }),
    shallow,
  );

  const { routeToViewSong } = useApp();
  const { songs } = useReplicacheSongs();
  const { songId } = useParams() as { songId: SongId };
  const song = songs[songId];

  const [isInitialized, setIsInitialized] = useState<number>(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState<boolean>(false);
  const [autoPlayStartTime, setAutoPlayStartTime] = useState<number>(0);
  const [isUserScrolling, setIsUserScrolling] = useState<boolean>(false);
  const autoPlayTimeout = useRef<number | undefined>(0);
  const kickoffTimeout = useRef<number | undefined>(0);
  const isUserScrollingTimeout = useRef<number | undefined>(0);

  const belowPx = scrollElement.scrollHeight - scrollElement.clientHeight;
  const durationMs = durationSeconds * 1000;

  const kickOffPlay = useCallback(async () => {
    console.debug(LOG_TAG, "kickOffPlay");
    scrollElement.scrollTo(0, 0);
    await enterFullscreen();
    kickoffTimeout.current = window.setTimeout(() => {
      kickoffTimeout.current = undefined;
      setAutoPlayStartTime(Date.now());
    }, delayKickoffMs);
    setIsInitialized(Date.now());
  }, [scrollElement]);

  useEffect(() => {
    console.debug(LOG_TAG, "MOUNT", isInitialized);
    if (isInitialized) {
      return;
    }
    kickOffPlay();
    return () => {
      console.debug(LOG_TAG, "UNMOUNT");
      setIsAutoPlaying(false);
    };
  }, [isInitialized, kickOffPlay]);

  // when autoPlayStartTime changes
  useEffect(() => {
    if (autoPlayStartTime === undefined) {
      return;
    }
    console.debug("autoPlayStartTime changed", { autoPlayStartTime });
    if (!autoPlayStartTime) {
      return;
    }
    window.analyticsLogEvent("SongViewAutoPlay start");
    setIsAutoPlaying(true);
  }, [autoPlayStartTime]);

  const routeBackToViewSong = () => {
    exitFullscreen();
    setIsAutoPlaying(false);
    routeToViewSong(song);
  };

  // when isAutoPlaying changes
  useEffect(() => {
    if (isAutoPlaying === undefined) {
      return;
    }
    console.debug(LOG_TAG, "isAutoPlaying changed", {
      isAutoPlaying,
    });
    if (!isAutoPlaying) {
      const clearTimeouts = () => {
        console.debug("clearTimeouts");
        window.clearTimeout(autoPlayTimeout.current);
        window.clearTimeout(kickoffTimeout.current);
        window.clearTimeout(isUserScrollingTimeout.current);
        kickoffTimeout.current = undefined;
        autoPlayTimeout.current = undefined;
        isUserScrollingTimeout.current = undefined;
      };
      clearTimeouts();
    } else if (isAutoPlaying) {
      const autoPlay = () => {
        LOG_VERBOSE &&
          console.debug(LOG_TAG, "autoPlay()", {
            autoPlayStartTime,
            autoPlayTimeout,
            isAutoPlaying,
            now: Date.now(),
            durationMs,
          });

        if (!isAutoPlaying) {
          throw new Error("SongViewAutoPlay autoPlay() but !isAutoPlaying");
        }

        const deltaMs = Date.now() - autoPlayStartTime;

        const scrollToCalc = Math.round((deltaMs / durationMs) * belowPx);
        const scrollTo = Math.min(scrollToCalc, belowPx);
        LOG_VERBOSE &&
          console.debug(LOG_TAG, "scrollTo:", scrollTo, {
            deltaMs,
            durationMs,
            belowPx,
            scrollToCalc,
            scrollHeight: scrollElement.scrollHeight,
            scrollTop: scrollElement.scrollTop,
          });

        scrollElement.scrollTo(0, scrollTo);

        if (scrollTo === belowPx) {
          console.log("SongViewAutoPlay completed", {
            durationMs,
            deltaMs,
            scrollTo,
            belowPx,
          });
          //window.analyticsLogEvent("SongViewAutoPlay completed");
          // routeBackToViewSong(); Steve wants it to stay =0
          return;
        }

        // next tick
        autoPlayTimeout.current = window.setTimeout(autoPlay, delayScrollMs);
      };
      autoPlay();
    }
    appSetIsAutoPlaying(isAutoPlaying);
  }, [
    appSetIsAutoPlaying,
    autoPlayStartTime,
    belowPx,
    durationMs,
    isAutoPlaying,
    scrollElement,
  ]);

  const pause = ({ source }: { source: string }) => {
    console.debug(LOG_TAG, "pause", { kickoffTimeout, source });
    if (kickoffTimeout.current) {
      window.clearTimeout(kickoffTimeout.current);
      kickoffTimeout.current = undefined;
    }
    setIsAutoPlaying(false);
    window.analyticsLogEvent("SongViewAutoPlay Pause", {
      source,
    });
  };

  const replay = () => {
    kickOffPlay();
    window.analyticsLogEvent("SongViewAutoPlay Replay");
  };

  const resume = ({ source }: { source: string }) => {
    console.debug(LOG_TAG, "resume", { source });
    if (scrollElement.scrollTop === belowPx) {
      replay();
      return;
    }
    const elapsedFromScrollMs = Math.round(
      (scrollElement.scrollTop / belowPx) * durationMs,
    );
    setAutoPlayStartTime(Date.now() - elapsedFromScrollMs);
    window.analyticsLogEvent("SongViewAutoPlay Resume", { source });
  };

  // Interactions that pause autoplay
  // - scrolling, by mouse or touch
  // - space bar
  // - esc key
  // - click
  const handleScroll = (e: React.WheelEvent | TouchEvent) => {
    if (!isAutoPlaying) {
      setIsUserScrolling(true);
      if (isUserScrollingTimeout.current) {
        window.clearTimeout(isUserScrollingTimeout.current);
      }
      isUserScrollingTimeout.current = window.setTimeout(
        () => setIsUserScrolling(false),
        250,
      );
      return;
    }
    console.debug(LOG_TAG, "handleScroll", { e, isAutoPlaying });
    pause({ source: e.type });
  };
  useEventListener("mousewheel", handleScroll, scrollElement);
  useEventListener("touchmove", handleScroll, scrollElement);

  const enterFullscreen = async () => {
    /*
    try {
      const fs = await document.documentElement.requestFullscreen();
      console.debug(LOG_TAG, "requestFullscreen", fs);
    } catch (error) {
      console.warn("SongViewAutoPlay requestFullscreen error", error);
    }
    */
    /*
    try {
      const locked = await window.screen.orientation.lock("landscape");
      console.debug(LOG_TAG, "screen.orientation.lock", locked);
    } catch (error) {
      console.warn("SongViewAutoPlay screen.orientation.lock error", error);
    }
    */
  };

  const exitFullscreen = async () => {
    /*
    try {
      await window.screen.orientation.unlock();
    } catch (error) {
      console.warn("SongViewAutoPlay screen.orientation.unlock error", error);
    }
    */
    /*
    try {
      (await document.fullscreenElement) && document.exitFullscreen();
    } catch (error) {
      console.warn("SongViewAutoPlay document.exitFullscreen error", error);
    }
    */
  };

  const handleKeyUp = (e: KeyboardEvent) => {
    console.debug(LOG_TAG, "handleKeyUp", e.key, e.code, {
      e,
      isAutoPlaying,
    });

    if (isAutoPlaying || isAutoPlaying === undefined) {
      if (e.key === "Escape" || e.code === "Space") {
        pause({ source: e.key === "Escape" ? e.key : e.code });
        return;
      }
    }
    if (!isAutoPlaying) {
      if (e.code === "Space") {
        resume({ source: e.code });
        return;
      }
    }
  };
  useEventListener("keyup", handleKeyUp);

  const handleClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    console.debug(LOG_TAG, "handleClick", {
      e,
      isAutoPlaying,
      kickoffTimeoutCurrent: kickoffTimeout.current,
    });
    if (kickoffTimeout.current) {
      return;
    }
    const source = "Click";
    if (isAutoPlaying || isAutoPlaying === undefined) {
      pause({ source });
    } else {
      resume({ source });
    }
  };
  useEventListener("click", handleClick);

  return (
    <>
      <AutoPlayScrim isUserScrolling={isUserScrolling} />
      {kickoffTimeout.current && (
        <AutoPlayCountdown key={kickoffTimeout.current} />
      )}
      {!isAutoPlaying && !kickoffTimeout.current && (
        <>
          <AutoPlayPauseActions
            onClickStop={routeBackToViewSong}
            onClickReplay={(e) => {
              e.stopPropagation();
              replay();
            }}
          />
          <AutoPlayPauseResume />
        </>
      )}
    </>
  );
}

const AutoPlayScrim = ({ isUserScrolling }: { isUserScrolling: boolean }) => {
  return (
    <div className="pointer-events-none">
      <div
        className="bg-[#0000008a] fixed top-0 right-0 left-0 z-10"
        style={{ height: `calc(50vh - ${scrimHeight / 2}px)` }}
      />
      <div
        className="fixed top-[50%] right-0 left-0 z-10 translate-y-[-50%]"
        style={{
          height: scrimHeight,
          background:
            "linear-gradient(to bottom, rgba(0,0,0,0.54) 0%, rgba(0,0,0,0.54) 20%, rgba(0,0,0,0) 40% 60%, rgba(0,0,0,0.54) 80%, rgba(0,0,0,0.54) 100%)",
        }}
      />
      {isUserScrolling && (
        <div
          className="fixed right-0 left-0 z-20 translate-y-[-50%] h-1 bg-[#00000033]"
          style={{
            top: `calc(50% + ${scrimHeight * 0.2}px)`,
          }}
        />
      )}
      <div
        className="fixed right-0 left-0 bottom-0 z-10 bg-[#0000008a]"
        style={{ top: `calc(50vh + ${scrimHeight / 2}px)` }}
      />
    </div>
  );
};

const AutoPlayPauseActions = ({
  onClickReplay,
  onClickStop,
}: {
  onClickReplay: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
  onClickStop: () => void;
}) => {
  return (
    <div className="fixed top-2 right-2 flex flex-col">
      <div className="btn btn-circle btn-ghost text-2xl" onClick={onClickStop}>
        <MdStop />
      </div>
      <div
        className="btn btn-circle btn-ghost text-2xl"
        onClick={onClickReplay}
      >
        <MdReplay />
      </div>
    </div>
  );
};

const AutoPlayPauseResume = () => {
  const fontSize = "5rem";
  //<PauseCircleOutlineIcon style={{ fontSize }} />
  return (
    <div
      className="text-accent-content fixed opacity-[0.54] top-1/2 left-1/2 translate-x-[-50%] translate-y-[-50%] z-30"
      style={{ animation: "$bounceIn 1s" }}
    >
      <MdPlayCircleOutline style={{ fontSize }} />
    </div>
  );
};

const AutoPlayCountdown = () => {
  const [counter, setCounter] = useState(delayKickoffSec);
  const timeoutRef = useRef<number>(0);
  useEffect(() => {
    console.debug("AutoPlayCountdown", { counter });
    if (counter > 0) {
      timeoutRef.current = window.setTimeout(
        () => setCounter(counter - 1),
        1000,
      );
    }
    return () => {
      window.clearTimeout(timeoutRef.current);
    };
  }, [counter]);

  if (!counter) return null;

  return (
    <div
      key={counter}
      className="text-accent-content fixed top-1/2 left-1/2 translate-x-[-50%] translate-y-[-50%] z-30"
      style={{ animation: "$bounceIn 1s" }}
    >
      {counter}
    </div>
  );
};
